import { useEffect, useState } from 'react';
import { polyvoxUrl, trackPolyvoxClick, hasVisitedPolyvox } from '../utils/polyvox';
import { trackEvent } from '../utils/analytics';

const SHOW_DELAY_MS = 1500;

/**
 * Polyvox ad shown as a pop-up after a detection or a finished Langle.
 * Every change of `trigger` (a truthy value) opens it again, until the
 * visitor clicks through to usepolyvox.com once — then it never returns.
 * The short delay lets the result land before the pop-up covers it.
 */
export default function PolyvoxPopup({ placement, language, trigger }) {
  const [open, setOpen] = useState(false);
  const lang = typeof language === 'string' ? language.trim() : '';

  useEffect(() => {
    if (!trigger || hasVisitedPolyvox()) return undefined;
    const t = setTimeout(() => {
      setOpen(true);
      trackEvent('polyvox_popup_shown', { placement, ...(lang ? { language: lang } : {}) });
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
    // Only a new trigger should reopen it, not a language/placement change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const popupPlacement = `${placement}_popup`;
  const headline = lang ? `Hear your voice speak ${lang}` : 'Speak any language in your own voice';

  return (
    <div className="lng-modal-overlay" onClick={() => setOpen(false)}>
      <div
        className="lng-modal polyvox-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="polyvox-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="lng-modal-close" aria-label="Close" onClick={() => setOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <p className="polyvox-kicker">Also from us · Polyvox</p>
        <p className="polyvox-headline" id="polyvox-popup-title">{headline}</p>
        <p className="polyvox-body">
          Clone a 30-second sample and hear it in 75 languages — same voice, native pronunciation.
        </p>
        <div className="polyvox-popup-actions">
          <a
            className="polyvox-cta"
            href={polyvoxUrl(popupPlacement)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackPolyvoxClick(popupPlacement, lang ? { language: lang } : undefined);
              setOpen(false);
            }}
          >
            Try Polyvox
          </a>
          <button className="polyvox-popup-later" onClick={() => setOpen(false)}>
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
